import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import StripePayment from '../payment/StripePayment';

interface Package {
  id: number;
  name: string;
  price: number;
  currency: string;
  features: string[];
  popular: boolean;
  country_name: string;
  country_flag: string;
}

interface Partner {
  name: string;
  address: string;
  percentage: number;
  passport_file?: File;
}

interface RequestFormData {
  package_id: number;
  company_name: string;
  another_name: string;
  company_industry: string;
  company_email: string;
  usa_number: string;
  company_website: string;
  owner_name: string;
  owner_phone: string;
  owner_address: string;
  partners: Partner[];
  owner_passport?: File;
}

const RequestWizard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState<Package[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [createdRequest, setCreatedRequest] = useState<any>(null);
  
  const [formData, setFormData] = useState<RequestFormData>({
    package_id: 0,
    company_name: '',
    another_name: '',
    company_industry: '',
    company_email: '',
    usa_number: '',
    company_website: '',
    owner_name: '',
    owner_phone: '',
    owner_address: '',
    partners: []
  });

  const industries = [
    'Accounting', 'Advertising', 'Agriculture', 'Art', 'Theater',
    'Beauty and Cosmetic Services', 'Biotech', 'Business Management',
    'Cleaning Services', 'Construction', 'Consulting', 'E-commerce',
    'Education', 'Entertainment', 'Fashion', 'Finance', 'Food & Beverage',
    'Healthcare', 'Hospitality', 'Insurance', 'Legal Services',
    'Manufacturing', 'Marketing', 'Media', 'Non-profit', 'Real Estate',
    'Retail', 'Technology', 'Transportation', 'Travel & Tourism'
  ];

  const partnershipPercentages = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50];

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPackages();
      setPackages(response.packages || []);
    } catch (error) {    } finally {
      setLoading(false);
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 1:
        if (!formData.package_id) {
          newErrors.package_id = t('validation.required');
        }
        break;

      case 2:
        if (!formData.company_name) {
          newErrors.company_name = t('validation.required');
        }
        if (!formData.company_industry) {
          newErrors.company_industry = t('validation.required');
        }
        break;

      case 3:
        if (!formData.owner_name) {
          newErrors.owner_name = t('validation.required');
        }
        if (!formData.owner_phone) {
          newErrors.owner_phone = t('validation.required');
        }
        if (!formData.owner_address) {
          newErrors.owner_address = t('validation.required');
        }
        if (!formData.owner_passport) {
          newErrors.owner_passport = t('validation.required');
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleInputChange = (field: keyof RequestFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileChange = (field: string, file: File) => {
    setFormData(prev => ({ ...prev, [field]: file }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const addPartner = () => {
    setFormData(prev => ({
      ...prev,
      partners: [...prev.partners, { name: '', address: '', percentage: 5 }]
    }));
  };

  const removePartner = (index: number) => {
    setFormData(prev => ({
      ...prev,
      partners: prev.partners.filter((_, i) => i !== index)
    }));
  };

  const updatePartner = (index: number, field: keyof Partner, value: any) => {
    setFormData(prev => ({
      ...prev,
      partners: prev.partners.map((partner, i) => 
        i === index ? { ...partner, [field]: value } : partner
      )
    }));
  };

  const submitRequest = async () => {
    try {
      setLoading(true);
      
      const requestData = {
        package_id: formData.package_id,
        company_name: formData.company_name,
        another_name: formData.another_name,
        company_industry: formData.company_industry,
        company_email: formData.company_email,
        usa_number: formData.usa_number,
        company_website: formData.company_website,
        owner_name: formData.owner_name,
        owner_phone: formData.owner_phone,
        owner_address: formData.owner_address,
        partners: formData.partners.map(p => ({
          name: p.name,
          address: p.address,
          percentage: p.percentage
        }))
      };

      const response = await fetch('/backend/api/requests/create.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(requestData)
      });

      const result = await response.json();
      
      if (result.success) {
        // Upload documents
        if (formData.owner_passport) {
          await uploadDocument(result.request.id, 'owner_passport', formData.owner_passport);
        }

        // Upload partner passports
        for (let i = 0; i < formData.partners.length; i++) {
          if (formData.partners[i].passport_file) {
            await uploadDocument(result.request.id, 'partner_passport', formData.partners[i].passport_file!);
          }
        }

        navigate('/dashboard/requests', {
          state: { 
            message: 'Request created successfully!',
            requestNumber: result.request.request_number
          }
        });
      } else {
        throw new Error(result.error || 'Failed to create request');
      }
    } catch (error) {      setErrors({ submit: 'Failed to submit request. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const uploadDocument = async (requestId: number, documentType: string, file: File) => {
    const formDataUpload = new FormData();
    formDataUpload.append('document', file);
    formDataUpload.append('request_id', requestId.toString());
    formDataUpload.append('document_type', documentType);

    const response = await fetch('/backend/api/requests/upload.php', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: formDataUpload
    });

    if (!response.ok) {
      throw new Error('Failed to upload document');
    }
  };

  const selectedPackage = packages.find(p => p.id === formData.package_id);

  const steps = [
    { number: 1, title: 'Package Selection', completed: currentStep > 1 },
    { number: 2, title: 'Company Information', completed: currentStep > 2 },
    { number: 3, title: 'Owner Information', completed: currentStep > 3 },
    { number: 4, title: 'Add Partners (Optional)', completed: currentStep > 4 },
    { number: 5, title: 'Review & Submit', completed: currentStep > 5 }
  ];

  if (loading && packages.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.number} className="flex items-center">
              <div className={`
                w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium
                ${step.completed ? 'bg-green-500 text-white' : 
                  currentStep === step.number ? 'bg-blue-500 text-white' : 
                  'bg-gray-200 text-gray-600'}
              `}>
                {step.completed ? '✓' : step.number}
              </div>
              <span className={`ml-2 text-sm ${step.completed || currentStep === step.number ? 'font-medium' : 'text-gray-500'}`}>
                {step.title}
              </span>
              {index < steps.length - 1 && (
                <div className={`mx-4 flex-1 h-px ${step.completed ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        {/* Step 1: Package Selection */}
        {currentStep === 1 && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Select Package</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {packages.map((pkg) => (
                <div
                  key={pkg.id}
                  onClick={() => handleInputChange('package_id', pkg.id)}
                  className={`
                    border-2 rounded-lg p-6 cursor-pointer transition-all
                    ${formData.package_id === pkg.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}
                    ${!!pkg.popular ? 'ring-2 ring-yellow-400' : ''}
                  `}
                >
                  {!!pkg.popular && (
                    <div className="bg-yellow-400 text-black px-2 py-1 rounded text-xs font-medium mb-2 inline-block">
                      Recommended
                    </div>
                  )}
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">{pkg.country_flag}</span>
                    <h3 className="text-lg font-semibold">{pkg.name}</h3>
                  </div>
                  <p className="text-gray-600 mb-2">{pkg.country_name}</p>
                  <div className="text-2xl font-bold text-blue-600 mb-4">
                    ${pkg.price} {pkg.currency}
                  </div>
                  <ul className="text-sm space-y-1">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-green-500 mr-2">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            {errors.package_id && (
              <p className="text-red-500 text-sm mt-2">{errors.package_id}</p>
            )}
          </div>
        )}

        {/* Step 2: Company Information */}
        {currentStep === 2 && (
          <div>
            <h2 className="text-2xl font-bold mb-6">{t('request.company_information')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('request.company_name')} *
                </label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                {errors.company_name && (
                  <p className="text-red-500 text-sm mt-1">{errors.company_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('request.another_name')}
                </label>
                <input
                  type="text"
                  value={formData.another_name}
                  onChange={(e) => handleInputChange('another_name', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('request.company_industry')} *
                </label>
                <select
                  value={formData.company_industry}
                  onChange={(e) => handleInputChange('company_industry', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">{t('request.select_industry')}</option>
                  {industries.map((industry) => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                </select>
                {errors.company_industry && (
                  <p className="text-red-500 text-sm mt-1">{errors.company_industry}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('request.company_email')}
                </label>
                <input
                  type="email"
                  value={formData.company_email}
                  onChange={(e) => handleInputChange('company_email', e.target.value)}
                  placeholder="support@domain.com"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('request.usa_number')}
                </label>
                <input
                  type="tel"
                  value={formData.usa_number}
                  onChange={(e) => handleInputChange('usa_number', e.target.value)}
                  placeholder="+1********"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('request.company_website')}
                </label>
                <input
                  type="url"
                  value={formData.company_website}
                  onChange={(e) => handleInputChange('company_website', e.target.value)}
                  placeholder="Website URL"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Owner Information */}
        {currentStep === 3 && (
          <div>
            <h2 className="text-2xl font-bold mb-6">{t('request.owner_information')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('request.owner_name')} *
                </label>
                <input
                  type="text"
                  value={formData.owner_name}
                  onChange={(e) => handleInputChange('owner_name', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                {errors.owner_name && (
                  <p className="text-red-500 text-sm mt-1">{errors.owner_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('request.owner_phone')} *
                </label>
                <input
                  type="tel"
                  value={formData.owner_phone}
                  onChange={(e) => handleInputChange('owner_phone', e.target.value)}
                  placeholder="country code + number"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                {errors.owner_phone && (
                  <p className="text-red-500 text-sm mt-1">{errors.owner_phone}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('request.owner_address')} * 
                  <span className="text-gray-500 text-xs ml-2">({t('request.english_only')})</span>
                </label>
                <textarea
                  value={formData.owner_address}
                  onChange={(e) => handleInputChange('owner_address', e.target.value)}
                  placeholder="address, zip code, country"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  required
                />
                {errors.owner_address && (
                  <p className="text-red-500 text-sm mt-1">{errors.owner_address}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('request.owner_passport')} * 
                  <span className="text-gray-500 text-xs ml-2">(.jpeg, .png, .webp {t('request.only')})</span>
                </label>
                <input
                  type="file"
                  accept=".jpeg,.jpg,.png,.webp"
                  onChange={(e) => e.target.files && handleFileChange('owner_passport', e.target.files[0])}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                {errors.owner_passport && (
                  <p className="text-red-500 text-sm mt-1">{errors.owner_passport}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Partners (Optional) */}
        {currentStep === 4 && (
          <div>
            <h2 className="text-2xl font-bold mb-6">{t('request.add_partners')} ({t('request.optional')})</h2>
            
            {formData.partners.map((partner, index) => (
              <div key={index} className="border rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium">{t('request.partner')} {index + 1}</h3>
                  <button
                    onClick={() => removePartner(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    {t('request.remove')}
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('request.partner_name')}
                    </label>
                    <input
                      type="text"
                      value={partner.name}
                      onChange={(e) => updatePartner(index, 'name', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('request.ownership_percentage')}
                    </label>
                    <select
                      value={partner.percentage}
                      onChange={(e) => updatePartner(index, 'percentage', parseInt(e.target.value))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {partnershipPercentages.map((percent) => (
                        <option key={percent} value={percent}>{percent}%</option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('request.partner_address')}
                    </label>
                    <textarea
                      value={partner.address}
                      onChange={(e) => updatePartner(index, 'address', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={2}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('request.partner_passport')} (.jpeg, .png, .webp {t('request.only')})
                    </label>
                    <input
                      type="file"
                      accept=".jpeg,.jpg,.png,.webp"
                      onChange={(e) => e.target.files && updatePartner(index, 'passport_file', e.target.files[0])}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            ))}

            <button
              onClick={addPartner}
              className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 mb-4"
            >
              {t('request.add_partner')}
            </button>
          </div>
        )}

        {/* Step 5: Review */}
        {currentStep === 5 && (
          <div>
            <h2 className="text-2xl font-bold mb-6">{t('request.review_information')}</h2>
            
            {/* Package Summary */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">{t('request.selected_package')}</h3>
              {selectedPackage && (
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center mb-2">
                      <span className="text-2xl mr-2">{selectedPackage.country_flag}</span>
                      <h4 className="text-lg font-medium">{selectedPackage.name}</h4>
                    </div>
                    <p className="text-gray-600">{selectedPackage.country_name}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-blue-600">
                      ${selectedPackage.price} {selectedPackage.currency}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Company Details */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">{t('request.company_details')}</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><strong>{t('request.company_name')}:</strong> {formData.company_name}</div>
                <div><strong>{t('request.industry')}:</strong> {formData.company_industry}</div>
                {formData.another_name && (
                  <div><strong>{t('request.another_name')}:</strong> {formData.another_name}</div>
                )}
                {formData.company_email && (
                  <div><strong>{t('request.email')}:</strong> {formData.company_email}</div>
                )}
                {formData.usa_number && (
                  <div><strong>{t('request.usa_number')}:</strong> {formData.usa_number}</div>
                )}
                {formData.company_website && (
                  <div><strong>{t('request.website')}:</strong> {formData.company_website}</div>
                )}
              </div>
            </div>

            {/* Owner Details */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">{t('request.owner_details')}</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><strong>{t('request.name')}:</strong> {formData.owner_name}</div>
                <div><strong>{t('request.phone')}:</strong> {formData.owner_phone}</div>
                <div className="col-span-2"><strong>{t('request.address')}:</strong> {formData.owner_address}</div>
              </div>
            </div>

            {/* Partners */}
            {formData.partners.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold mb-4">{t('request.partners')}</h3>
                {formData.partners.map((partner, index) => (
                  <div key={index} className="mb-3 pb-3 border-b border-gray-200 last:border-b-0">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div><strong>{t('request.name')}:</strong> {partner.name}</div>
                      <div><strong>{t('request.ownership')}:</strong> {partner.percentage}%</div>
                      <div className="col-span-2"><strong>{t('request.address')}:</strong> {partner.address}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          <button
            onClick={prevStep}
            disabled={currentStep === 1}
            className={`px-6 py-2 rounded-md ${
              currentStep === 1 
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                : 'bg-gray-500 text-white hover:bg-gray-600'
            }`}
          >
            {t('request.back')}
          </button>

          {currentStep < 5 ? (
            <button
              onClick={nextStep}
              disabled={loading}
              className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 disabled:opacity-50"
            >
              {loading ? t('common.loading') : t('request.continue')}
            </button>
          ) : (
            <button
              onClick={submitRequest}
              disabled={loading}
              className="bg-green-500 text-white px-6 py-2 rounded-md hover:bg-green-600 disabled:opacity-50"
            >
              {loading ? t('common.submitting') : t('request.submit')}
            </button>
          )}
        </div>

        {errors.submit && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-700">{errors.submit}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestWizard;